import {MediaPlayback} from '../model/SessionState';
import {SessionCommands} from '../model/SessionCommands';
import {SessionStore} from '../model/SessionStore';
import {AsyncSessionResult, SessionResult} from '../model/AsyncSessionResult';
import {Call, SfuModels} from '@stream-io/video-client';
import {logUtishaDebug, logUtishaError, logUtishaInfo, logUtishaWarn} from 'src/app/util/log';
import {CanvasRenderingPipeline} from './helpers/CanvasRenderingPipeline';
import {HTMLVideoElementCanvasRenderer} from './helpers/renderers/HTMLVideoElementCanvasRenderer';
import {MediaPlaybackCanvasRenderer} from './helpers/renderers/MediaPlaybackCanvasRenderer';
import {TimerCanvasRenderer} from './helpers/renderers/TimerCanvasRenderer';
import {Timer, TimerOptions} from '../views/timer';
import {ZoomOptions} from '../model/ZoomOptions';
import {ZoomableCanvasRenderer} from './helpers/renderers/ZoomableCanvasRenderer';
import {debounce, skip} from 'rxjs/operators';
import {interval, Subscription} from 'rxjs';
import {AudioPublisher} from './AudioPublisher';
import {CpuPressureLevel} from '../model/PerformanceMonitor';
import {acquireCameraWithFallback} from './helpers/acquireMedia';

export class StreamMediaPublisher {
  private readonly MAIN_RENDERER_PRIORITY = 10;
  private readonly TIMER_BEEP = '/assets/audio/beep1.wav';
  private hiddenVideoPlayers: HTMLElement;
  private hiddenVideoPlayer: HTMLVideoElement;
  private videoStreamer = new CanvasRenderingPipeline();
  private audioPublisher: AudioPublisher;
  private subscriptions: Subscription[] = [];
  private verifySubscription?: Subscription;
  private _changingPipeline = false;
  // Operation serialization queue
  private _opChain: Promise<any> = Promise.resolve();
  private _opSeq = 0;
  private _directCameraStream: MediaStream | null = null;
  private _onLocalStreamChanged?: () => void;

  constructor(private call: Call, private commands: SessionCommands, private sessionStore: SessionStore) {
    const existing = document.querySelector('#hidden-video-players');
    if (!existing) {
      this.hiddenVideoPlayers = document.createElement('div');
      this.hiddenVideoPlayers.setAttribute('id', 'hidden-video-players');
      this.hiddenVideoPlayers.style.display = 'none';
      this.hiddenVideoPlayer = document.createElement('video');
      this.hiddenVideoPlayer.setAttribute('playsinline', '');
      this.hiddenVideoPlayer.setAttribute('muted', '');
      this.hiddenVideoPlayer.muted = true;
      this.hiddenVideoPlayers.appendChild(this.hiddenVideoPlayer);
      document.body.appendChild(this.hiddenVideoPlayers);
    } else {
      this.hiddenVideoPlayers = existing as HTMLElement;
      this.hiddenVideoPlayer = this.hiddenVideoPlayers.querySelector('video') || document.createElement('video');
    }
    // Initialize the audio publisher with the session state
    this.audioPublisher = new AudioPublisher(call, commands, sessionStore);
    this.registerListeners();
  }

  /** Register a callback invoked whenever the local video stream changes (canvas ↔ direct). */
  set onLocalStreamChanged(cb: (() => void) | undefined) {
    this._onLocalStreamChanged = cb;
  }

  async streamCameraUsingCanvas() {
    return this.enqueueOperation('streamCameraUsingCanvas', async () => this._streamCameraUsingCanvasImpl());
  }

  // Actual implementation (serialized via enqueueOperation)
  private async _streamCameraUsingCanvasImpl() {
    logUtishaDebug(`stream: streamCamera`);
    // Clear direct camera stream since we're switching to canvas mode
    this._directCameraStream = null;
    try {
      if (!this.startChangeOfPipeline()) {
        logUtishaWarn('Ignoring streamCameraUsingCanvas: pipeline change already in progress');
        return;
      }
      if (this.videoStreamer.hasRendererWithName(HTMLVideoElementCanvasRenderer.NAME) && this.videoStreamer.started) {
        logUtishaDebug(`Camera already streaming, nothing to do`);
        this.stopChangeOfPipeline();
        return;
      }

      const videoElement = await this.streamCameraToHiddenHTMLVideoElement();

      this.stopOtherMainRenderers(HTMLVideoElementCanvasRenderer.NAME);

      // Create and add the renderer
      const renderer = new HTMLVideoElementCanvasRenderer(videoElement, this.MAIN_RENDERER_PRIORITY);
      this.videoStreamer.addRenderer(renderer);
      await this.videoStreamer.start();

      // Before publishing, explicitly stop any existing tracks
      await this.safeStopPublish(SfuModels.TrackType.VIDEO);
      await this.publishCanvasStream();
      this._onLocalStreamChanged?.();
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error streaming camera: ', error);
      return SessionResult.failure(`Failed to stream video: ${error.message}`);
    } finally {
      this.stopChangeOfPipeline();
    }
  }

  private async publishCanvasStream() {
    try {
      await this.safePublish(this.videoStreamer.stream, SfuModels.TrackType.VIDEO);
      this.verifyMediaStream();
      this.sessionStore.update({localUser: {isVideoOn: true}});
    } catch (error: any) {
      logUtishaError('stream: publish failed in publishCanvasStream', error);
      this.sessionStore.update({localUser: {isVideoOn: false}});
      this.sessionStore.update({session: {error: 'Your camera couldn\'t connect. Others can\'t see you.'}});
      throw error;
    }
  }

  private stopOtherMainRenderers(exceptRendererName: string) {
    // clean up old main renderers first
    this.videoStreamer
      .searchRenderers({priority: this.MAIN_RENDERER_PRIORITY})
      .filter(renderer => renderer.name !== exceptRendererName)
      .forEach(renderer => renderer.destroy());

    // sync the session state
    switch (exceptRendererName) {
      case MediaPlaybackCanvasRenderer.NAME:
        if (this.sessionStore.snapshot.ui.zoom.enabled) {
          this.sessionStore.update({ui: {zoom: {enabled: false}}});
        }
        break;
      default:
        if (this.sessionStore.snapshot.media.mediaPlayback?.started) {
          this.commands.startMediaPlayback.request({started: false});
        }
    }
  }

  async startMediaPlayback(mediaPlayback: MediaPlayback): AsyncSessionResult {
    return this.enqueueOperation('startMediaPlayback', async () => this._startMediaPlaybackImpl(mediaPlayback));
  }

  private async _startMediaPlaybackImpl(mediaPlayback: MediaPlayback): AsyncSessionResult {
    logUtishaDebug(`Starting media playback of ${mediaPlayback.file?.url}`);
    try {
      if (!this.startChangeOfPipeline()) {
        logUtishaWarn('Ignoring startMediaPlayback: pipeline change already in progress');
        return SessionResult.failure('Another operation is in progress');
      }
      if (!this.call) {
        this.stopChangeOfPipeline();
        return SessionResult.failure('Call not initialized');
      }

      // Clear direct camera stream since we're switching to canvas mode
      this._directCameraStream = null;

      this.stopOtherMainRenderers(MediaPlaybackCanvasRenderer.NAME);
      const renderer = new MediaPlaybackCanvasRenderer(mediaPlayback);
      this.videoStreamer.addRenderer(renderer);
      await this.videoStreamer.start();
      // Keep the current camera feed alive
      await this.streamCameraToHiddenHTMLVideoElement();
      await this.safeStopPublish(SfuModels.TrackType.VIDEO);
      try {
        await this.safePublish(this.videoStreamer.stream, SfuModels.TrackType.VIDEO);
      } catch (publishError: any) {
        logUtishaError('stream: publish failed in startMediaPlayback', publishError);
        this.sessionStore.update({localUser: {isVideoOn: false}});
        throw publishError;
      }

      this.verifyMediaStream();
      this.sessionStore.update({localUser: {isVideoOn: true}});
      this._onLocalStreamChanged?.();
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error streaming video file: ', error);
      this.sessionStore.update({session: {error: `Failed to stream video: ${error.message}`}});
      return SessionResult.failure(`Failed to stream video: ${error.message}`);
    } finally {
      this.stopChangeOfPipeline();
    }
  }

  async stopMediaPlayback(mediaPlayback: MediaPlayback | undefined): AsyncSessionResult {
    return this.enqueueOperation('stopMediaPlayback', async () => this._stopMediaPlaybackImpl(mediaPlayback));
  }

  private async _stopMediaPlaybackImpl(mediaPlayback: MediaPlayback | undefined): AsyncSessionResult {
    logUtishaDebug(`Stopping media playback: ${JSON.stringify(mediaPlayback) || 'undefined'}`);
    if (!this.videoStreamer.hasRendererWithName(MediaPlaybackCanvasRenderer.NAME) || !this.videoStreamer.started) {
      logUtishaWarn(`Media playback not started, nothing to stop`);
      return SessionResult.ok;
    }

    try {
      if (!this.call) {
        return SessionResult.failure('Call not initialized');
      }
      await this.stopRenderer(MediaPlaybackCanvasRenderer.NAME);
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error stopping video:', error);
      this.sessionStore.update({session: {error: 'Failed to stop video'}});
      return SessionResult.failure('Failed to stop video');
    }
  }

  private verifyMediaStream() {
    this.verifySubscription?.unsubscribe();
    this.verifySubscription = this.call.camera.state.mediaStream$.subscribe((mediaStream) => {
      if (mediaStream) {
        const [videoTrack] = mediaStream.getVideoTracks();
        logUtishaDebug('Video track active:' + videoTrack);
      } else {
        logUtishaDebug('No media stream active');
      }
    });
  }

  async streamCameraWithoutCanvas() {
    return this.enqueueOperation('streamCameraWithoutCanvas', async () => this._streamCameraWithoutCanvasImpl());
  }

  private async _streamCameraWithoutCanvasImpl() {
    logUtishaDebug(`Publishing camera without using canvas`);
    this.videoStreamer.stop();
    // Disable the camera feed
    await this.safeStopPublish(SfuModels.TrackType.VIDEO);
    // Get the original camera that was active before playback
    const originalCamera = this.sessionStore.snapshot.localUser.camera;
    if (this.hiddenVideoPlayer?.srcObject) {
      // The SDK automatically publishes the camera stream once enabled.
      // Use the fallback ladder so a stale deviceId or rejected constraint
      // degrades gracefully instead of killing the pipeline switch.
      const cameraStream = await acquireCameraWithFallback({deviceId: originalCamera?.deviceId});
      this._directCameraStream = cameraStream;
      await this.safePublish(cameraStream, SfuModels.TrackType.VIDEO);
      // Stop source tracks before clearing the reference so the camera device
      // is freed immediately — prevents contention when zoom re-enters later.
      (this.hiddenVideoPlayer.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      this.hiddenVideoPlayer.srcObject = null;
      this._onLocalStreamChanged?.();
    } else {
      this._directCameraStream = null;
      this.sessionStore.update({localUser: {isVideoOn: false}});
    }
  }

  async stopRenderer(name: string) {
    logUtishaDebug(`Stopping renderer ${name}`);
    const renderersToBeStopped = this.videoStreamer.getRenderersWithName(name);
    renderersToBeStopped.forEach(renderer => renderer.destroy());
    const canvasStillRequired = this.videoStreamer.searchRenderers({excludingName: HTMLVideoElementCanvasRenderer.NAME}).length > 0;

    if (canvasStillRequired) {
      logUtishaDebug(`Canvas still required, not stopping video streamer`);
      if (renderersToBeStopped.find(renderer => renderer.priority === this.MAIN_RENDERER_PRIORITY)) {
        logUtishaDebug(`Switching to camera as main renderer on canvas`);
        // Call impl directly to avoid enqueue deadlocks inside an enqueued operation
        await this._streamCameraUsingCanvasImpl();
      }
      return;
    }
    // Call impl directly to avoid enqueue deadlocks inside an enqueued operation
    await this._streamCameraWithoutCanvasImpl();
  }

  private async streamCameraToHiddenHTMLVideoElement() {
    // Always ensure a camera stream is attached to the hidden video element
    try {
      if (!this.hiddenVideoPlayer.srcObject) {
        const camera = this.sessionStore.snapshot.localUser.camera;
        logUtishaDebug('Acquiring camera for hidden video element');
        // Fallback ladder: saved device HD → saved device SD → any HD → any
        // SD → any default. NotReadableError retries once per rung. Permission
        // denials short-circuit and throw up.
        const stream = await acquireCameraWithFallback({deviceId: camera?.deviceId});
        this.hiddenVideoPlayer.srcObject = stream;
      }
    } catch (e: any) {
      logUtishaError('Failed to acquire camera stream for hidden video element', e);
      throw e;
    }

    // Ensure attributes for autoplay compliance
    this.hiddenVideoPlayer.setAttribute('playsinline', '');
    this.hiddenVideoPlayer.setAttribute('muted', '');
    this.hiddenVideoPlayer.muted = true;

    // Ensure video is playing
    if (this.hiddenVideoPlayer.paused) {
      try {
        await this.hiddenVideoPlayer.play();
        logUtishaDebug('Hidden video successfully started playing');
      } catch (e: any) {
        throw new Error('Failed to play hidden video: ' + e.message);
      }
    }
    // Log current dimensions (may be 0 initially until metadata loaded)
    logUtishaDebug(`Hidden video dimensions: ${this.hiddenVideoPlayer.videoWidth}x${this.hiddenVideoPlayer.videoHeight}`);
    return this.hiddenVideoPlayer;
  }


  private async startTimer(seconds: number) {
    // Timer operations also go through the queue for determinism
    return this.enqueueOperation('startTimer', async () => this._startTimerImpl(seconds));
  }

  private async _startTimerImpl(seconds: number) {
    logUtishaDebug(`stream: startTimer ${seconds}`);
    if (!this.videoStreamer.started) {
      // Call impl directly to avoid enqueue deadlocks inside an enqueued operation
      await this._streamCameraUsingCanvasImpl();
    }
    this.videoStreamer.getRenderersWithName(TimerCanvasRenderer.NAME)
      .forEach(renderer => renderer.destroy());

    this.videoStreamer.addRenderer(new TimerCanvasRenderer(this.calculateTimerOptions(seconds)));
  }

  private calculateTimerOptions(seconds: number): TimerOptions {
    const canvas = this.videoStreamer.canvas;
    if (!canvas) {
      throw new Error('Canvas not initialized');
    }
    const width = Math.min(canvas.width, canvas.height) * 0.15;
    return {
      timeMs: seconds * 1000,
      ringWidth: width * 0.13,
      dimensions: {topLeft: {x: width * 0.25, y: width * 0.25}, width},
      beepOn: [3, 2, 1],
      beepAudioSource: this.audioPublisher.addAudio(this.TIMER_BEEP, 'Timer Beep'),
      onStopped: (timer: Timer) => {
        this.sessionStore.update({ui: {timer: { started: false, seconds: this.sessionStore.snapshot.ui.timer?.seconds || 0}}})
      },
    } as TimerOptions;
  }

  private async stopTimer() {
    return this.enqueueOperation('stopTimer', async () => this._stopTimerImpl());
  }

  private async _stopTimerImpl() {
    logUtishaDebug(`stream: stopTimer`);
    if (!this.videoStreamer.hasRendererWithName(TimerCanvasRenderer.NAME) || !this.videoStreamer.started) {
      logUtishaWarn(`Timer not running, nothing to stop`);
      return;
    }
    await this.stopRenderer(TimerCanvasRenderer.NAME);
    this.audioPublisher.removeAudio(this.TIMER_BEEP);
  }

  /**
   * Connects an audio source to the stream to mix it with microphone audio
   * @param audioSource The audio source to connect
   */


  private async startZoom(options: ZoomOptions) {
    return this.enqueueOperation('startZoom', async () => this._startZoomImpl(options));
  }

  private async _startZoomImpl(options: ZoomOptions) {
    logUtishaDebug(`stream: startZoom`);
    // Clear any direct camera stream so localVideoStream returns the canvas
    // stream after zoom starts (mirrors _streamCameraUsingCanvasImpl pattern).
    this._directCameraStream = null;
    try {
      if (!this.startChangeOfPipeline()) {
        logUtishaWarn('Ignoring startZoom: pipeline change already in progress');
        return;
      }
      const existingZoomRenderers = this.videoStreamer.getRenderersWithName(ZoomableCanvasRenderer.NAME);
      if (existingZoomRenderers.length > 0 && this.videoStreamer.started) {
        (existingZoomRenderers[0] as ZoomableCanvasRenderer).setZoomOptions(options);
        this.stopChangeOfPipeline();
        return;
      }

      const videoElement = await this.streamCameraToHiddenHTMLVideoElement();

      this.stopOtherMainRenderers(ZoomableCanvasRenderer.NAME);
      // Create and add the renderer
      const renderer = ZoomableCanvasRenderer.createWithVideoElement(videoElement, this.MAIN_RENDERER_PRIORITY, options);
      this.videoStreamer.addRenderer(renderer);
      await this.videoStreamer.start();

      await this.safeStopPublish(SfuModels.TrackType.VIDEO);
      await this.publishCanvasStream();
      this._onLocalStreamChanged?.();
      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error streaming camera: ', error);
      return SessionResult.failure(`Failed to stream zoomed video: ${error.message}`);
    } finally {
      this.stopChangeOfPipeline();
    }
  }

  private async stopZoom() {
    return this.enqueueOperation('stopZoom', async () => this._stopZoomImpl());
  }

  private async _stopZoomImpl() {
    logUtishaDebug(`stream: stopZoom`);
    if (!this.videoStreamer.hasRendererWithName(ZoomableCanvasRenderer.NAME) || !this.videoStreamer.started) {
      logUtishaWarn(`Zoom not enabled, nothing to stop`);
      return;
    }
    await this.stopRenderer(ZoomableCanvasRenderer.NAME);
  }

  private registerListeners() {
    const sub1 = this.commands.startMediaPlayback.requestTx
      .pipe(debounce(() => interval(300)))
      .subscribe(async (payload) => {
        if (payload?.started) {
          await this.startMediaPlayback(payload);
        } else {
          await this.stopMediaPlayback(payload);
        }
      });
    if (sub1) this.subscriptions.push(sub1);

    this.subscriptions.push(this.sessionStore.timer$
      .pipe(skip(1), debounce(() => interval(300)))
      .subscribe(async (timerStarted) => {
        if (timerStarted?.started && timerStarted?.seconds) {
          await this.startTimer(timerStarted.seconds);
        } else {
          await this.stopTimer();
        }
      }));
    this.subscriptions.push(this.sessionStore.zoom$
      .pipe(skip(1), debounce(() => interval(300)))
      .subscribe(async (options) => {
        if (options.enabled) {
          await this.startZoom(options);
        } else {
          await this.stopZoom();
        }
      }));
  }

  getCpuUsageScore(): number {
    return this.videoStreamer.getCpuUsageScore();
  }

  /**
   * Milliseconds since the canvas pipeline last drew a frame. Returns
   * Infinity when the pipeline isn't running (not "stalled", just stopped).
   * Consumed by MediaWatchdog to detect silent-black-frame stalls.
   */
  msSinceLastVideoFrame(): number {
    if (!this.videoStreamer.started) return Infinity;
    const last = this.videoStreamer.lastDrawTimestamp;
    if (last === 0) return Infinity; // Not yet drawn any frame
    return performance.now() - last;
  }

  /** True while the canvas pipeline is running. */
  get isCanvasPipelineRunning(): boolean {
    return this.videoStreamer.started;
  }

  adaptCanvasResolution(level: CpuPressureLevel): void {
    const canvas = this.videoStreamer.canvas;
    if (!canvas) return;

    let targetWidth: number;
    let targetHeight: number;

    switch (level) {
      case 'normal':
        targetWidth = 1280;
        targetHeight = 720;
        break;
      case 'reduced':
        targetWidth = 960;
        targetHeight = 540;
        break;
      case 'minimal':
        targetWidth = 640;
        targetHeight = 360;
        break;
    }

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      logUtishaInfo(`stream: canvas resolution adapted to ${targetWidth}x${targetHeight} (pressure=${level})`);
    }
  }

  /**
   * Returns the current local video stream being published.
   * This is the canvas pipeline stream when the canvas is active,
   * or the direct camera stream when publishing without canvas.
   */
  get localVideoStream(): MediaStream | null {
    if (this.videoStreamer.started && this.videoStreamer.stream) {
      return this.videoStreamer.stream;
    }
    return this._directCameraStream;
  }

  /**
   * Returns the raw camera MediaStreamTrack feeding the pipeline — NOT the
   * canvas output. Lives on `hiddenVideoPlayer.srcObject` in canvas mode,
   * on `_directCameraStream` in direct-publish mode. Used by MediaWatchdog
   * to detect source-side death that the canvas would otherwise hide by
   * happily redrawing the last decoded frame (classic iOS background-
   * suspend signature: `track.muted = true` with the pipeline still
   * ticking).
   */
  get sourceCameraVideoTrack(): MediaStreamTrack | null {
    // `srcObject` is a `MediaProvider` union (MediaStream | MediaStreamTrack
    // | Blob | null); only a MediaStream exposes getVideoTracks().
    const hiddenSrc = this.hiddenVideoPlayer?.srcObject;
    if (hiddenSrc instanceof MediaStream) {
      const hiddenTrack = hiddenSrc.getVideoTracks()[0];
      if (hiddenTrack) return hiddenTrack;
    }
    return this._directCameraStream?.getVideoTracks()[0] ?? null;
  }

  async stopVideo() {
    return this.enqueueOperation('stopVideo', async () => {
      logUtishaDebug('stream: stopVideo via publisher');
      this.videoStreamer.stop();
      await this.safeStopPublish(SfuModels.TrackType.VIDEO);
      if (this.hiddenVideoPlayer?.srcObject) {
        (this.hiddenVideoPlayer.srcObject as MediaStream).getTracks().forEach(t => t.stop());
        this.hiddenVideoPlayer.srcObject = null;
      }
      this._directCameraStream = null;
      this.sessionStore.update({localUser: {isVideoOn: false}});
    });
  }

  destroy() {
    if (this._directCameraStream) {
      this._directCameraStream.getTracks().forEach(t => t.stop());
      this._directCameraStream = null;
    }
    this.videoStreamer.stop();
    this.audioPublisher.dispose();
    this.verifySubscription?.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];

    if (this.hiddenVideoPlayer?.srcObject) {
      (this.hiddenVideoPlayer.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      this.hiddenVideoPlayer.srcObject = null;
    }
    this.hiddenVideoPlayers?.remove();
  }

  private startChangeOfPipeline(): boolean {
    if (this._changingPipeline) {
      return false;
    }
    this._changingPipeline = true;
    return true;
  }

  private stopChangeOfPipeline() {
    this._changingPipeline = false;
  }

  // Enqueue operations to ensure serialized pipeline transitions
  private enqueueOperation<T>(name: string, op: () => Promise<T>): Promise<T> {
    const opId = `${Date.now().toString(36)}-${(++this._opSeq & 0xfff).toString(36)}`;
    const run = async () => {
      logUtishaDebug(`[op:${opId}] BEGIN ${name}`);
      try {
        const result = await op();
        logUtishaDebug(`[op:${opId}] END ${name}`);
        return result as T;
      } catch (e: any) {
        logUtishaError(`[op:${opId}] ERROR ${name}`, e?.message || e);
        throw e;
      }
    };
    // Chain the operation; ensure chain doesn't break on errors
    const next = this._opChain.then(run, run);
    this._opChain = next.catch(() => {});
    return next;
  }

  // Centralized helpers to make (un)publishing more robust
  private async safeStopPublish(type: SfuModels.TrackType) {
    try {
      await this.call.stopPublish(type);
    } catch (err: any) {
      logUtishaWarn(`stopPublish(${SfuModels.TrackType[type]}) failed once: ${err?.message || err}`);
      // best-effort retry once after a short wait
      await new Promise(r => setTimeout(r, 150));
      try {
        await this.call.stopPublish(type);
      } catch (err2: any) {
        logUtishaError(`stopPublish(${SfuModels.TrackType[type]}) failed on retry: ${err2?.message || err2}`);
      }
    }
  }

  private async safePublish(stream: MediaStream, type: SfuModels.TrackType) {
    const tryOnce = async () => {
      await this.call.publish(stream, type);
      // simple verification for video publish
      if (type === SfuModels.TrackType.VIDEO) {
        const hasTrack = stream.getVideoTracks().length > 0;
        if (!hasTrack) throw new Error('No video track to publish');
      }
    };
    try {
      await tryOnce();
    } catch (err: any) {
      logUtishaWarn(`publish(${SfuModels.TrackType[type]}) failed once: ${err?.message || err}`);
      await new Promise(r => setTimeout(r, 200));
      try {
        await tryOnce();
      } catch (retryErr: any) {
        logUtishaError(`publish(${SfuModels.TrackType[type]}) failed on retry`, retryErr);
        throw new Error(`Failed to publish ${SfuModels.TrackType[type]} after retry: ${retryErr?.message || retryErr}`);
      }
    }
  }
}
